import { Box } from '@mui/material';
import { styles } from '../styles';
import type { PreMatchWidgetData } from '../types';

import type { JSX } from 'react';

export type SportCourtType =
  | 'soccer'
  | 'basketball'
  | 'tennis'
  | 'table-tennis'
  | 'handball'
  | 'volleyball'
  | 'ice-hockey'
  | 'water-polo'
  | 'baseball';

const normalizeSportValue = (value?: string | null) => value?.toLowerCase().replace(/[_\s-]+/g, '-') ?? '';

export const getSportCourtType = (sport?: PreMatchWidgetData['sport']): SportCourtType => {
  const normalized = [sport?.icon, sport?.name, sport?.id].map(normalizeSportValue).join('|');

  if (normalized.includes('basket')) return 'basketball';
  if (normalized.includes('table-tennis')) return 'table-tennis';
  if (normalized.includes('tennis')) return 'tennis';
  if (normalized.includes('handball') || normalized.includes('rukomet') || normalized.includes('ruomet'))
    return 'handball';
  if (normalized.includes('volley') || normalized.includes('odboj')) return 'volleyball';
  if (normalized.includes('ice-hockey') || normalized.includes('hockey') || normalized.includes('hokej'))
    return 'ice-hockey';
  if (normalized.includes('water-polo') || normalized.includes('vaterpolo')) return 'water-polo';
  if (normalized.includes('baseball') || normalized.includes('bejzbol')) return 'baseball';

  return 'soccer';
};

const SoccerCourt = () => (
  <>
    <path d='M19 11h482v270H19z' />
    <path d='M260 11v270' />
    <circle cx='260' cy='146' r='41' />
    <path d='M19 67h72v156H19z' />
    <path d='M19 106h27v79H19z' />
    <path d='M7 123h12v46H7z' opacity='0.6' />
    <circle cx='68' cy='146' r='2' fill='currentColor' stroke='none' />
    <path d='M91 112.06A44 44 0 0 1 107 146a44 44 0 0 1-16 33.94' />
    <path d='M501 67h-72v156h72z' />
    <path d='M501 106h-27v79h27z' />
    <path d='M513 123h-12v46h12z' opacity='0.6' />
    <circle cx='452' cy='146' r='2' fill='currentColor' stroke='none' />
    <path d='M429 112.06A44 44 0 0 0 413 146a44 44 0 0 0 16 33.94' />
    <path d='M28 11a9 9 0 0 1-9 9' />
    <path d='M28 281a9 9 0 0 0-9-9' />
    <path d='M492 11a9 9 0 0 0 9 9' />
    <path d='M492 281a9 9 0 0 1 9-9' />
  </>
);

const BasketballCourt = () => (
  <>
    <rect x='19' y='82' width='86' height='128' fill='var(--pmw-court-accent)' opacity='0.72' stroke='none' />
    <rect x='415' y='82' width='86' height='128' fill='var(--pmw-court-accent)' opacity='0.72' stroke='none' />
    <circle cx='260' cy='146' r='34' fill='var(--pmw-court-accent)' opacity='0.72' stroke='none' />
    <path d='M19 11h482v270H19z' />
    <path d='M260 11v270' />
    <circle cx='260' cy='146' r='34' />
    <path d='M19 82h86v128H19zM501 82h-86v128h86z' />
    <path d='M105 102a44 44 0 0 1 0 88M415 102a44 44 0 0 0 0 88' />
    <path d='M105 102a44 44 0 0 0 0 88M415 102a44 44 0 0 1 0 88' strokeDasharray='8 8' opacity='0.82' />
    <path d='M19 44h100a132 132 0 0 1 0 204H19M501 44H401a132 132 0 0 0 0 204h100' />
    <path d='M19 40h482M19 252h482' opacity='0.8' />
    <path d='M193 11v10M327 11v10M193 271v10M327 271v10' opacity='0.8' />
    <path d='M38 128v36M482 128v36' opacity='0.7' />
    <circle cx='47' cy='146' r='7' />
    <circle cx='473' cy='146' r='7' />
  </>
);

const TennisCourt = () => (
  <>
    <path d='M19 24h482v244H19z' />
    <path d='M19 55h482M19 237h482' />
    <path d='M126 55v182M394 55v182' />
    <path d='M126 146h268' />
    <path d='M260 55v182' />
    <path d='M260 20v252' opacity='0.9' />
    <circle cx='260' cy='20' r='3' fill='currentColor' stroke='none' />
    <circle cx='260' cy='272' r='3' fill='currentColor' stroke='none' />
  </>
);

const TableTennisCourt = () => (
  <>
    <path d='M19 24h482v244H19z' />
    <path d='M260 24v244' />
    <path d='M19 146h482' opacity='0.72' />
    <path d='M260 20v252' opacity='0.9' />
    <circle cx='260' cy='20' r='3' fill='currentColor' stroke='none' />
    <circle cx='260' cy='272' r='3' fill='currentColor' stroke='none' />
  </>
);

const HandballCourt = () => (
  <>
    <path d='M19 24h482v244H19z' />
    <path d='M260 24v244' />
    <circle cx='260' cy='146' r='28' />
    <path d='M19 60a86 86 0 0 1 0 172M501 60a86 86 0 0 0 0 172' />
    <path d='M81 24a122 122 0 0 1 0 244M439 24a122 122 0 0 0 0 244' strokeDasharray='10 10' />
    <path d='M86 141v10M128 141v10M392 141v10M434 141v10' />
    <path d='M7 129h12v34H7zM501 129h12v34h-12z' opacity='0.65' />
  </>
);

const VolleyballCourt = () => (
  <>
    <rect x='19' y='24' width='482' height='244' fill='var(--pmw-court-accent)' opacity='0.82' stroke='none' />
    <path d='M19 24h482v244H19z' />
    <path d='M260 24v244' />
    <path d='M182 24v244M338 24v244' />
    <path d='M19 58h482M19 234h482' opacity='0.75' />
    <path d='M10 24v244M510 24v244M19 14h482M19 278h482' strokeDasharray='6 8' opacity='0.68' />
    <path d='M260 18v256' opacity='0.9' />
    <circle cx='260' cy='18' r='2.5' fill='currentColor' stroke='none' />
    <circle cx='260' cy='274' r='2.5' fill='currentColor' stroke='none' />
  </>
);

const IceHockeyCourt = () => (
  <>
    <path d='M72 11h376a53 53 0 0 1 53 53v164a53 53 0 0 1-53 53H72a53 53 0 0 1-53-53V64a53 53 0 0 1 53-53Z' />
    <path
      d='M93 24h334a39 39 0 0 1 39 39v166a39 39 0 0 1-39 39H93a39 39 0 0 1-39-39V63a39 39 0 0 1 39-39Z'
      opacity='0.72'
    />
    <path d='M139 24v244M381 24v244' stroke='var(--pmw-court-blue)' />
    <path d='M260 24v244' stroke='var(--pmw-court-red)' />
    <path d='M72 24v244M448 24v244' stroke='var(--pmw-court-red)' />
    <circle cx='260' cy='146' r='34' stroke='var(--pmw-court-blue)' />
    <circle cx='260' cy='146' r='2.5' fill='var(--pmw-court-blue)' stroke='none' />
    <path d='M236 24a24 24 0 0 0 48 0M236 268a24 24 0 0 1 48 0' stroke='var(--pmw-court-red)' />
    <circle cx='124' cy='80' r='34' stroke='var(--pmw-court-red)' />
    <circle cx='124' cy='212' r='34' stroke='var(--pmw-court-red)' />
    <circle cx='396' cy='80' r='34' stroke='var(--pmw-court-red)' />
    <circle cx='396' cy='212' r='34' stroke='var(--pmw-court-red)' />
    <path
      d='M124 58v-7M124 102v7M102 80h-7M146 80h7M124 190v-7M124 234v7M102 212h-7M146 212h7M396 58v-7M396 102v7M374 80h-7M418 80h7M396 190v-7M396 234v7M374 212h-7M418 212h7'
      stroke='var(--pmw-court-red)'
    />
    <path
      d='M114 75h20M114 85h20M119 70v20M129 70v20M386 75h20M386 85h20M391 70v20M401 70v20M114 207h20M114 217h20M119 202v20M129 202v20M386 207h20M386 217h20M391 202v20M401 202v20'
      stroke='var(--pmw-court-red)'
      opacity='0.82'
    />
    <circle cx='211' cy='80' r='3' fill='var(--pmw-court-red)' stroke='none' />
    <circle cx='211' cy='212' r='3' fill='var(--pmw-court-red)' stroke='none' />
    <circle cx='309' cy='80' r='3' fill='var(--pmw-court-red)' stroke='none' />
    <circle cx='309' cy='212' r='3' fill='var(--pmw-court-red)' stroke='none' />
    <path
      d='M54 129h20a20 20 0 0 1 0 34H54M466 129h-20a20 20 0 0 0 0 34h20'
      fill='var(--pmw-court-blue-fill)'
      stroke='var(--pmw-court-red)'
    />
  </>
);

const WaterPoloCourt = () => (
  <>
    <path d='M19 33h482v226H19z' />
    <path d='M260 33v226' />
    <path d='M88 33v226M432 33v226' opacity='0.8' />
    <path d='M19 106h42v80H19zM501 106h-42v80h42z' />
    <path
      d='M34 66c32 8 64 8 96 0s64-8 96 0 64 8 96 0 64-8 96 0M34 226c32-8 64-8 96 0s64 8 96 0 64-8 96 0 64 8 96 0'
      opacity='0.45'
    />
  </>
);

const BaseballField = () => (
  <>
    <path d='M260 258 88 146 260 34l172 112-172 112Z' />
    <path d='M260 258V34M88 146h344' />
    <path d='M260 258 188 146 260 86l72 60-72 112Z' opacity='0.8' />
    <circle cx='260' cy='146' r='58' />
    <path d='M248 246h24v24h-24zM76 134h24v24H76zM248 22h24v24h-24zM420 134h24v24h-24z' />
    <path d='M240 239 100 154M280 239l140-85' opacity='0.55' />
  </>
);

const COURT_GRAPHICS: Record<SportCourtType, JSX.Element> = {
  soccer: <SoccerCourt />,
  basketball: <BasketballCourt />,
  tennis: <TennisCourt />,
  'table-tennis': <TableTennisCourt />,
  handball: <HandballCourt />,
  volleyball: <VolleyballCourt />,
  'ice-hockey': <IceHockeyCourt />,
  'water-polo': <WaterPoloCourt />,
  baseball: <BaseballField />,
};

type PitchFieldGraphicProps = {
  sport?: PreMatchWidgetData['sport'];
};

const PitchFieldGraphic = ({ sport }: PitchFieldGraphicProps) => {
  const courtType = getSportCourtType(sport);

  return (
    <Box component='svg' viewBox='0 0 520 292' preserveAspectRatio='none' aria-hidden='true' sx={styles.pitchSvg}>
      <g opacity='0.86' fill='none' stroke='currentColor' strokeWidth='2' vectorEffect='non-scaling-stroke'>
        {COURT_GRAPHICS[courtType]}
      </g>
    </Box>
  );
};

export default PitchFieldGraphic;
